import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ChevronDown, ChevronRight, Minus, Plus, RefreshCw, TriangleAlert } from "lucide-react";
import { cn } from "@nexus/ui";
import type {
  CascadePreview,
  CascadeTenantPreview,
} from "@/features/identity-hub/api/identity-hub.api";
import {
  affectedCascadeTenants,
  cascadeTenantChange,
  describeCascadePermission,
  type CascadeTenantChange,
} from "@/features/identity-hub/lib/cascade-preview";
import { OrgLogoMark } from "./OrgLogoMark";
import type { OrgPickerItem } from "./OrgPickerCombobox";

interface TemplateCascadePreviewProps {
  preview: CascadePreview;
  orgs: OrgPickerItem[];
}

type PermissionTuple = CascadeTenantPreview["diff"]["added"][number];

const CHANGE_BADGE_CLASS: Record<CascadeTenantChange, string> = {
  new: "bg-success-subtle text-success-subtle-foreground",
  updated: "bg-primary-subtle text-primary-subtle-foreground",
  removed: "bg-error-subtle text-error-subtle-foreground",
  failed: "bg-error-subtle text-error-subtle-foreground",
};

/** Step 2 of a catalog-role save: what the cascade will change in each linked organization. */
export function TemplateCascadePreview({ preview, orgs }: TemplateCascadePreviewProps) {
  const { t } = useTranslation();
  const rows = affectedCascadeTenants(preview);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(rows[0] ? [rows[0].tenantId] : []),
  );
  const orgById = new Map(orgs.map((org) => [org.id, org]));
  const allExpanded = rows.length > 0 && rows.every((row) => expandedIds.has(row.tenantId));

  const toggleRow = (tenantId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  };

  return (
    <div
      ref={(node) => { if (node) node.focus(); }}
      tabIndex={-1}
      role="status"
      aria-live="polite"
      className="flex flex-col gap-4 outline-none"
    >
      <p className="text-sm text-foreground">
        {rows.length === 0
          ? t("iam.templates.dialog.previewNoChanges", "Saving doesn't change access in any organization.")
          : t("iam.templates.dialog.previewSummary", {
              count: rows.length,
              total: preview.tenants.length,
              name: preview.name,
              defaultValue: "Saving updates {{count}} of the {{total}} organizations that have {{name}}.",
            })}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <PreviewStat
          Icon={Plus}
          iconClassName="bg-success-subtle text-success-subtle-foreground"
          value={preview.addedTenantCount}
          label={t("iam.templates.dialog.previewStatAdded", "Gets this role")}
        />
        <PreviewStat
          Icon={RefreshCw}
          iconClassName="bg-primary-subtle text-primary"
          value={preview.updatedGroupCount}
          label={t("iam.templates.dialog.previewStatUpdated", "Permissions change")}
        />
        <PreviewStat
          Icon={Minus}
          iconClassName="bg-error-subtle text-error-subtle-foreground"
          value={preview.removedTenantCount}
          label={t("iam.templates.dialog.previewStatRemoved", "Loses this role")}
        />
      </div>

      {preview.holderExpansionCount > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warning-border bg-warning-subtle px-3.5 py-3 text-warning-subtle-foreground">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-medium">
              {t("iam.templates.dialog.previewHoldersTitle", {
                count: preview.holderExpansionCount,
                defaultValue: "{{count}} people will switch to this role",
              })}
            </span>
            <span className="text-xs">
              {t(
                "iam.templates.dialog.previewHoldersBody",
                "They already hold this role elsewhere. Their current role in the new organizations is replaced.",
              )}
            </span>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <section className="flex flex-col gap-2" aria-label={t("iam.templates.dialog.previewByOrg", "Changes by organization")}>
          <div className="flex items-baseline justify-between">
            <h4 className="text-[13px] font-semibold text-foreground">
              {t("iam.templates.dialog.previewByOrg", "Changes by organization")}
            </h4>
            <button
              type="button"
              onClick={() => setExpandedIds(allExpanded ? new Set() : new Set(rows.map((row) => row.tenantId)))}
              className="rounded text-xs font-medium text-primary outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              {allExpanded
                ? t("iam.templates.dialog.previewCollapseAll", "Collapse all")
                : t("iam.templates.dialog.previewExpandAll", "Expand all")}
            </button>
          </div>
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {rows.map((row) => (
              <PreviewOrgRow
                key={row.tenantId}
                row={row}
                org={orgById.get(row.tenantId)}
                isExpanded={expandedIds.has(row.tenantId)}
                onToggle={() => toggleRow(row.tenantId)}
                t={t}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PreviewStat({
  Icon,
  iconClassName,
  value,
  label,
}: {
  Icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  value: number;
  label: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border px-3.5 py-3">
      <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-full", iconClassName)}>
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="flex flex-col">
        <span className="text-lg font-semibold tabular-nums text-foreground">{value}</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </span>
    </div>
  );
}

function PreviewOrgRow({
  row,
  org,
  isExpanded,
  onToggle,
  t,
}: {
  row: CascadeTenantPreview;
  org: OrgPickerItem | undefined;
  isExpanded: boolean;
  onToggle: () => void;
  t: TFunction;
}) {
  const change = cascadeTenantChange(row);
  const orgName = org?.name ?? row.tenantId;
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <li className="flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex min-h-11 items-center gap-3 px-3.5 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <OrgLogoMark
          name={orgName}
          logoUrl={org?.logoUrl}
          accent={org?.brandColor}
          isSuperTenant={org?.type === "SUPER_TENANT"}
          size={24}
        />
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{orgName}</span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{changeSummary(t, row, change)}</span>
        <span className={cn("shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium", CHANGE_BADGE_CLASS[change])}>
          {changeLabel(t, change)}
        </span>
      </button>
      {isExpanded && (
        <div className="mx-3.5 mb-3 grid grid-cols-1 gap-4 rounded-md bg-muted px-3.5 py-2.5 sm:grid-cols-2">
          <PermissionDiffList
            heading={t("iam.templates.dialog.previewAddedHeading", "Added")}
            headingClassName="text-success-subtle-foreground"
            Icon={Plus}
            iconClassName="text-success-subtle-foreground"
            tuples={row.diff.added}
            t={t}
          />
          <PermissionDiffList
            heading={t("iam.templates.dialog.previewRemovedHeading", "Removed")}
            headingClassName="text-error-subtle-foreground"
            Icon={Minus}
            iconClassName="text-error-subtle-foreground"
            tuples={row.diff.removed}
            t={t}
          />
        </div>
      )}
    </li>
  );
}

function PermissionDiffList({
  heading,
  headingClassName,
  Icon,
  iconClassName,
  tuples,
  t,
}: {
  heading: string;
  headingClassName: string;
  Icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  tuples: PermissionTuple[];
  t: TFunction;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className={cn("text-[11px] font-medium uppercase tracking-label", headingClassName)}>{heading}</span>
      {tuples.length === 0 ? (
        <span className="text-xs text-muted-foreground">{t("iam.templates.dialog.previewNone", "Nothing")}</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {tuples.map((tuple) => {
            const label = describeCascadePermission(tuple);
            return (
              <li key={`${tuple.type}|${tuple.method}|${tuple.route}`} className="flex min-w-0 items-baseline gap-2">
                <Icon className={cn("h-3 w-3 shrink-0 self-center", iconClassName)} aria-hidden />
                <span className="min-w-0 break-words text-xs text-foreground">
                  {label?.name ?? `${tuple.method} ${tuple.route}`}
                </span>
                {label && (
                  <span className="min-w-0 truncate text-[11px] text-muted-foreground">
                    {label.groupLabel
                      ? t("iam.permissionEditor.searchLocation", "{{area}} › {{group}}", {
                          area: label.areaLabel,
                          group: label.groupLabel,
                        })
                      : label.areaLabel}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function changeLabel(t: TFunction, change: CascadeTenantChange): string {
  if (change === "new") return t("iam.templates.dialog.previewChangeNew", "New");
  if (change === "removed") return t("iam.templates.dialog.previewChangeRemoved", "Removed");
  if (change === "failed") return t("iam.templates.dialog.previewChangeFailed", "Can't update");
  return t("iam.templates.dialog.previewChangeUpdated", "Updated");
}

function changeSummary(t: TFunction, row: CascadeTenantPreview, change: CascadeTenantChange): string {
  if (change === "new") {
    return t("iam.templates.dialog.previewNewGroup", {
      count: row.diff.added.length,
      defaultValue: "New access group · {{count}} permissions",
    });
  }
  if (change === "removed") return t("iam.templates.dialog.previewRemovedGroup", "Access group removed");
  return t("iam.templates.dialog.previewDiffSummary", "{{added}} added · {{removed}} removed", {
    added: row.diff.added.length,
    removed: row.diff.removed.length,
  });
}
